/* ═══════════════════════════════════════════════════════════
   Artworld Studio — Custom Art Feature
   ═══════════════════════════════════════════════════════════ */

/* ─── SECTION A: APP STATE ─────────────────────────────── */
const state = {
  currentStep: 1,
  uploadedPhoto: null,
  uploadedPhotoUrl: null,
  selectedStyle: null,
  generatedResults: [],
  selectedResult: null,
  selectedSize: null,
  regenerationsLeft: 2,
  isLoading: false,
  dzineStyleCodes: {}
};

/* ─── SECTION C: STYLE DATA ─────────────────────────────── */
const STYLES = [
  {
    id: 'oil-painting',
    name: 'Oil Painting',
    description: 'Rich textures, warm tones, classical feel',
    prompt: 'classical oil painting with rich impasto textures, warm tones and visible brushstrokes, museum quality fine art',
    dzineStyleCode: null
  },
  {
    id: 'charcoal-sketch',
    name: 'Charcoal Sketch',
    description: 'Dramatic shadows, fine detail, timeless look',
    prompt: 'detailed charcoal sketch with dramatic shadows, fine cross hatching and expressive lines on textured paper, fine art quality',
    dzineStyleCode: null
  },
  {
    id: 'watercolour',
    name: 'Watercolour',
    description: 'Soft flowing colours, delicate and dreamy',
    prompt: 'soft flowing watercolour painting with delicate washes of colour, wet on wet bleeding edges, light and airy feel',
    dzineStyleCode: null
  },
  {
    id: 'street-art',
    name: 'Street Art',
    description: 'Bold urban energy, Banksy-inspired edge',
    prompt: 'bold urban street art in the style of Banksy, high contrast stencil art with gritty texture and rebellious energy',
    dzineStyleCode: null
  },
  {
    id: 'african-art',
    name: 'African Art',
    description: 'Vibrant patterns, rich colour, cultural depth',
    prompt: 'vibrant traditional African art style with bold geometric patterns, rich earthy colours and cultural symbolic elements',
    dzineStyleCode: null
  },
  {
    id: 'black-white',
    name: 'Black & White',
    description: 'High contrast, cinematic, dramatic and elegant',
    prompt: 'dramatic high contrast black and white fine art photography with deep blacks, bright whites and cinematic mood',
    dzineStyleCode: null
  },
  {
    id: 'pop-art',
    name: 'Pop Art',
    description: 'Bold colour, graphic impact, Andy Warhol energy',
    prompt: 'bold graphic pop art in the style of Andy Warhol with vivid flat colours, halftone dots and high contrast graphic style',
    dzineStyleCode: null
  },
  {
    id: 'impressionist',
    name: 'Impressionist',
    description: 'Painterly, soft light, Monet-inspired beauty',
    prompt: 'Monet style impressionist oil painting with loose visible brushstrokes, soft dappled light and painterly texture',
    dzineStyleCode: null
  }
];

/* ─── SECTION D: SIZE DATA ─────────────────────────────── */
const SIZES = [
  { id: 'A4', dimensions: '210 × 297mm', price: 380, displayPrice: 'R380',  shopifyVariantId: 52092853059885, visual: { w: 30, h: 42 } },
  { id: 'A3', dimensions: '297 × 420mm', price: 580, displayPrice: 'R580',  shopifyVariantId: 52092855812397, visual: { w: 42, h: 60 } },
  { id: 'A2', dimensions: '420 × 594mm', price: 860, displayPrice: 'R860',  shopifyVariantId: 52092855845165, visual: { w: 52, h: 74 } },
  { id: 'A1', dimensions: '594 × 841mm', price: 1400, displayPrice: 'R1,400', shopifyVariantId: 52092855877933, visual: { w: 62, h: 88 } },
  { id: 'A0', dimensions: '841 × 1,189mm', price: 2500, displayPrice: 'R2,500', shopifyVariantId: 52092855910701, visual: { w: 72, h: 100 } }
];

/* ─── SECTION B: HTML TEMPLATE ──────────────────────────── */
function buildHTML() {
  const styleCards = STYLES.map(s => `
    <div class="aws-style-card" data-style-id="${s.id}" role="button" tabindex="0" aria-label="${s.name}">
      <div class="aws-style-name">${s.name}</div>
      <div class="aws-style-desc">${s.description}</div>
    </div>`).join('');

  const sizeCards = SIZES.map(s => `
    <div class="aws-size-card" data-size-id="${s.id}" role="button" tabindex="0" aria-label="${s.id} canvas">
      <div class="aws-size-id">${s.id}</div>
      <div class="aws-size-dims">${s.dimensions}</div>
      <div class="aws-size-visual" style="width:${s.visual.w * 0.7}px;height:${s.visual.h * 0.7}px;"></div>
      <div class="aws-size-price">${s.displayPrice}</div>
    </div>`).join('');

  const inspoCards = Array.from({ length: 6 }, () =>
    `<div class="aws-inspo-card">🎨</div>`).join('');

  return `
    <section class="aws-hero">
      <h1 class="aws-hero-title">ARTWORLD STUDIO</h1>
      <p class="aws-hero-tagline">Turn any photo into a gallery-quality canvas. Delivered to your door.</p>
      <div class="aws-trust-badges">
        <div class="aws-badge"><span class="aws-badge-icon">🎨</span><span>8 Art Styles</span></div>
        <div class="aws-badge"><span class="aws-badge-icon">📐</span><span>5 Canvas Sizes</span></div>
        <div class="aws-badge"><span class="aws-badge-icon">🚚</span><span>Nationwide Delivery</span></div>
      </div>
    </section>

    <div class="aws-progress" role="progressbar" aria-label="Order steps">
      <div class="aws-progress-step active" data-step="1">
        <span class="aws-step-num">1</span>
        <span class="aws-step-label">Upload</span>
      </div>
      <div class="aws-progress-line"></div>
      <div class="aws-progress-step" data-step="2">
        <span class="aws-step-num">2</span>
        <span class="aws-step-label">Style</span>
      </div>
      <div class="aws-progress-line"></div>
      <div class="aws-progress-step" data-step="3">
        <span class="aws-step-num">3</span>
        <span class="aws-step-label">Generate</span>
      </div>
      <div class="aws-progress-line"></div>
      <div class="aws-progress-step" data-step="4">
        <span class="aws-step-num">4</span>
        <span class="aws-step-label">Order</span>
      </div>
    </div>

    <!-- STEP 1: UPLOAD -->
    <section class="aws-step active" data-step="1">
      <h2>Upload Your Photo</h2>
      <p class="aws-step-intro">Any photo works — portraits, pets, landscapes, buildings or objects</p>
      <div class="aws-upload-zone" id="uploadZone" role="button" tabindex="0" aria-label="Upload photo">
        <div class="aws-upload-icon">⬆</div>
        <p class="aws-upload-text">Drag &amp; drop your photo here</p>
        <p class="aws-upload-sub">or tap to browse your photos</p>
        <p class="aws-upload-formats">JPG · PNG · HEIC · Max 20MB</p>
        <input type="file" id="photoInput" accept=".jpg,.jpeg,.png,.heic" style="display:none" aria-hidden="true">
      </div>
      <div class="aws-upload-preview" id="uploadPreview" style="display:none">
        <img id="previewImg" src="" alt="Your uploaded photo">
        <button class="aws-remove-photo" id="removePhoto" aria-label="Remove photo">✕</button>
      </div>
      <div class="aws-error" id="uploadError" style="display:none" role="alert"></div>
      <button class="aws-next-btn" id="toStep2" disabled>Choose Your Style →</button>
    </section>

    <!-- STEP 2: STYLE -->
    <section class="aws-step" data-step="2">
      <h2>Choose Your Art Style</h2>
      <p class="aws-step-intro">Select the style you want your photo transformed into</p>
      <div class="aws-styles-grid" id="stylesGrid">${styleCards}</div>
      <button class="aws-next-btn" id="toStep3" disabled>Generate My Artwork →</button>
    </section>

    <!-- STEP 3: GENERATE -->
    <section class="aws-step" data-step="3">
      <div class="aws-loading" id="loadingState">
        <div class="aws-loading-icon">🎨</div>
        <div class="aws-loading-bar">
          <div class="aws-loading-fill" id="loadingFill"></div>
        </div>
        <p class="aws-loading-msg" id="loadingMsg">Transforming your photo...</p>
        <p class="aws-loading-time">This takes about 30–60 seconds</p>
      </div>
      <div class="aws-results" id="resultsState" style="display:none">
        <h2>Choose Your Favourite</h2>
        <p class="aws-step-intro">3 unique variations of your photo</p>
        <div class="aws-results-grid" id="resultsGrid"></div>
        <button class="aws-regen-btn" id="regenBtn">
          Not happy? Regenerate ↺ (<span id="regenCount">2</span> remaining)
        </button>
        <button class="aws-next-btn" id="toStep4" disabled>Choose Size &amp; Order →</button>
      </div>
    </section>

    <!-- STEP 4: SIZE & ORDER -->
    <section class="aws-step" data-step="4">
      <h2>Choose Your Canvas Size</h2>
      <div class="aws-chosen-preview">
        <img id="chosenArtwork" src="" alt="Your chosen artwork">
        <p class="aws-chosen-label">Your chosen artwork</p>
      </div>
      <div class="aws-sizes-grid" id="sizesGrid">${sizeCards}</div>
      <p class="aws-delivery-note">All prints are mounted canvas, ready to hang. Delivered nationwide. Allow 5–7 working days.</p>
      <div class="aws-price-display">
        <span class="aws-price" id="priceDisplay">Select a size</span>
        <span class="aws-price-note">Includes printing, mounting &amp; delivery</span>
      </div>
      <button class="aws-order-btn" id="orderBtn" disabled>
        Order My Artwork — R<span id="orderPrice">0</span> →
      </button>
      <p class="aws-free-delivery">✓ Free delivery on all Studio orders</p>
    </section>

    <section class="aws-inspiration">
      <h2>See What's Possible</h2>
      <p>Real transformations from Artworld Studio</p>
      <div class="aws-inspo-grid" id="inspoGrid">${inspoCards}</div>
    </section>
  `;
}

/* ─── SECTION E: DZINE API ──────────────────────────────── */
const LOADING_MESSAGES = [
  'Transforming your photo...',
  'Applying the style...',
  'Creating 3 unique variations...',
  'Adding fine details...',
  'Almost ready...',
  'Finishing touches...'
];

async function fetchDzineStyles() {
  try {
    const res = await fetch('/apps/artworld-studio/styles');
    if (!res.ok) return;
    const data = await res.json();
    if (!data.styles) return;
    STYLES.forEach(style => {
      const match = data.styles.find(s =>
        s.name && s.name.toLowerCase().includes(style.id.replace('-', ' '))
      );
      if (match) style.dzineStyleCode = match.style_code || match.id || null;
    });
  } catch (_) {
    // Styles will fall back to prompt-only generation
  }
}

async function generateArtwork() {
  state.isLoading = true;
  showLoading(true);
  startLoadingAnimation();

  try {
    const res = await fetch('/apps/artworld-studio/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        photoUrl: state.uploadedPhotoUrl,
        styleCode: state.selectedStyle.dzineStyleCode,
        prompt: state.selectedStyle.prompt
      })
    });

    if (!res.ok) throw new Error('Generation failed');
    const data = await res.json();

    if (data.images && data.images.length) {
      state.generatedResults = data.images;
      showResults(data.images);
    } else {
      throw new Error('No images returned');
    }
  } catch (err) {
    showLoading(false);
    showGenerationError('Something went wrong. Please try again.');
  } finally {
    state.isLoading = false;
    stopLoadingAnimation();
  }
}

let loadingMsgInterval = null;
let loadingFillInterval = null;
let loadingMsgIndex = 0;
let loadingFillValue = 0;

function startLoadingAnimation() {
  loadingMsgIndex = 0;
  loadingFillValue = 0;

  const fill = document.getElementById('loadingFill');
  const msg  = document.getElementById('loadingMsg');
  if (fill) fill.style.width = '0%';

  loadingMsgInterval = setInterval(() => {
    loadingMsgIndex = (loadingMsgIndex + 1) % LOADING_MESSAGES.length;
    if (msg) {
      msg.style.opacity = '0';
      setTimeout(() => {
        msg.textContent = LOADING_MESSAGES[loadingMsgIndex];
        msg.style.opacity = '1';
      }, 200);
    }
  }, 4000);

  loadingFillInterval = setInterval(() => {
    const target = loadingFillValue < 85 ? loadingFillValue + 2 : loadingFillValue + 0.2;
    loadingFillValue = Math.min(target, 92);
    if (fill) fill.style.width = loadingFillValue + '%';
  }, 800);
}

function stopLoadingAnimation() {
  clearInterval(loadingMsgInterval);
  clearInterval(loadingFillInterval);
  const fill = document.getElementById('loadingFill');
  if (fill) fill.style.width = '100%';
}

function showLoading(show) {
  const loading = document.getElementById('loadingState');
  const results = document.getElementById('resultsState');
  if (!loading || !results) return;
  loading.style.display = show ? 'flex' : 'none';
  results.style.display = show ? 'none'  : 'block';
}

function showResults(images) {
  showLoading(false);
  const grid = document.getElementById('resultsGrid');
  if (!grid) return;
  grid.innerHTML = images.map((url, i) => `
    <div class="aws-result-card" data-index="${i}" role="button" tabindex="0" aria-label="Variation ${i + 1}">
      <img src="${url}" alt="Artwork variation ${i + 1}" loading="lazy">
      <button class="aws-select-btn">Select This One</button>
    </div>`).join('');

  grid.querySelectorAll('.aws-result-card').forEach(card => {
    card.addEventListener('click', () => selectResult(parseInt(card.dataset.index)));
    card.addEventListener('keydown', e => { if (e.key === 'Enter') selectResult(parseInt(card.dataset.index)); });
  });
}

function showGenerationError(msg) {
  const loading = document.getElementById('loadingState');
  if (!loading) return;
  loading.innerHTML = `
    <p style="color:#e06060;font-size:16px;text-align:center;">${msg}</p>
    <button class="aws-next-btn" onclick="generateArtwork()" style="margin-top:16px;">Try Again →</button>
  `;
}

function selectResult(index) {
  state.selectedResult = state.generatedResults[index];
  document.querySelectorAll('.aws-result-card').forEach((c, i) => {
    c.classList.toggle('selected', i === index);
  });
  const btn = document.getElementById('toStep4');
  if (btn) btn.disabled = false;
}

/* ─── SECTION F: PHOTO UPLOAD ───────────────────────────── */
function initUpload() {
  const zone   = document.getElementById('uploadZone');
  const input  = document.getElementById('photoInput');
  const remove = document.getElementById('removePhoto');

  zone.addEventListener('click', () => input.click());
  zone.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') input.click(); });

  zone.addEventListener('dragover', e => { e.preventDefault(); zone.classList.add('drag-over'); });
  zone.addEventListener('dragleave', () => zone.classList.remove('drag-over'));
  zone.addEventListener('drop', e => {
    e.preventDefault();
    zone.classList.remove('drag-over');
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  });

  input.addEventListener('change', () => {
    if (input.files[0]) handleFile(input.files[0]);
  });

  remove.addEventListener('click', e => {
    e.stopPropagation();
    clearUpload();
  });
}

function handleFile(file) {
  const errEl = document.getElementById('uploadError');
  const validTypes = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];
  const MAX_MB = 20;

  errEl.style.display = 'none';

  if (!validTypes.includes(file.type) && !file.name.match(/\.(heic|heif)$/i)) {
    showUploadError('Please upload a JPG, PNG or HEIC photo.');
    return;
  }
  if (file.size > MAX_MB * 1024 * 1024) {
    showUploadError(`Photo must be under ${MAX_MB}MB.`);
    return;
  }

  compressAndPreview(file);
}

function compressAndPreview(file) {
  const reader = new FileReader();
  reader.onload = e => {
    const img = new Image();
    img.onload = () => {
      const MAX_PX = 1800;
      let { width, height } = img;
      if (width > MAX_PX || height > MAX_PX) {
        const ratio = Math.min(MAX_PX / width, MAX_PX / height);
        width  = Math.round(width  * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);

      canvas.toBlob(blob => {
        state.uploadedPhoto    = blob;
        state.uploadedPhotoUrl = canvas.toDataURL('image/jpeg', 0.85);
        showPreview(state.uploadedPhotoUrl);
      }, 'image/jpeg', 0.85);
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
}

function showPreview(url) {
  document.getElementById('uploadZone').style.display    = 'none';
  document.getElementById('uploadPreview').style.display = 'block';
  document.getElementById('previewImg').src = url;
  document.getElementById('uploadError').style.display  = 'none';
  document.getElementById('toStep2').disabled = false;
}

function clearUpload() {
  state.uploadedPhoto    = null;
  state.uploadedPhotoUrl = null;
  document.getElementById('uploadZone').style.display    = 'flex';
  document.getElementById('uploadPreview').style.display = 'none';
  document.getElementById('photoInput').value = '';
  document.getElementById('toStep2').disabled = true;
}

function showUploadError(msg) {
  const el = document.getElementById('uploadError');
  el.textContent  = msg;
  el.style.display = 'block';
}

/* ─── SECTION G: NAVIGATION ─────────────────────────────── */
function goToStep(step) {
  document.querySelectorAll('.aws-step').forEach(el => el.classList.remove('active'));
  const target = document.querySelector(`.aws-step[data-step="${step}"]`);
  if (target) target.classList.add('active');

  document.querySelectorAll('.aws-progress-step').forEach(el => {
    const s = parseInt(el.dataset.step);
    el.classList.remove('active', 'completed');
    if (s === step) el.classList.add('active');
    else if (s < step) el.classList.add('completed');
  });

  state.currentStep = step;
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function initNavigation() {
  document.getElementById('toStep2').addEventListener('click', () => {
    if (!state.uploadedPhotoUrl) return;
    goToStep(2);
  });

  document.getElementById('toStep3').addEventListener('click', () => {
    if (!state.selectedStyle) return;
    goToStep(3);
    generateArtwork();
  });

  document.getElementById('toStep4').addEventListener('click', () => {
    if (!state.selectedResult) return;
    goToStep(4);
    const img = document.getElementById('chosenArtwork');
    if (img) img.src = state.selectedResult;
  });

  document.getElementById('regenBtn').addEventListener('click', () => {
    if (state.regenerationsLeft <= 0) return;
    state.regenerationsLeft--;
    document.getElementById('regenCount').textContent = state.regenerationsLeft;
    if (state.regenerationsLeft === 0) {
      document.getElementById('regenBtn').disabled = true;
    }
    state.selectedResult = null;
    document.getElementById('toStep4').disabled = true;
    showLoading(true);
    generateArtwork();
  });
}

/* ─── STYLE SELECTION ───────────────────────────────────── */
function initStyleSelection() {
  document.getElementById('stylesGrid').addEventListener('click', e => {
    const card = e.target.closest('.aws-style-card');
    if (!card) return;
    const styleId = card.dataset.styleId;
    state.selectedStyle = STYLES.find(s => s.id === styleId);
    document.querySelectorAll('.aws-style-card').forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');
    document.getElementById('toStep3').disabled = false;
  });

  document.getElementById('stylesGrid').addEventListener('keydown', e => {
    if (e.key === 'Enter' || e.key === ' ') {
      const card = e.target.closest('.aws-style-card');
      if (card) card.click();
    }
  });
}

/* ─── SIZE SELECTION ────────────────────────────────────── */
function initSizeSelection() {
  document.getElementById('sizesGrid').addEventListener('click', e => {
    const card = e.target.closest('.aws-size-card');
    if (!card) return;
    const sizeId = card.dataset.sizeId;
    state.selectedSize = SIZES.find(s => s.id === sizeId);
    document.querySelectorAll('.aws-size-card').forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');
    const price = state.selectedSize.displayPrice;
    document.getElementById('priceDisplay').textContent = price;
    document.getElementById('orderPrice').textContent = state.selectedSize.price.toLocaleString('en-ZA');
    document.getElementById('orderBtn').disabled = false;
  });
}

/* ─── SECTION H: SHOPIFY CART ───────────────────────────── */
async function addToCart() {
  if (!state.selectedSize || !state.selectedResult) return;
  const btn = document.getElementById('orderBtn');
  btn.disabled = true;
  btn.textContent = 'Adding to cart...';

  const variantId = state.selectedSize.shopifyVariantId;
  if (!variantId) {
    showCartError('Product not set up yet. Please contact us to order.');
    btn.disabled = false;
    btn.innerHTML = `Order My Artwork — R<span id="orderPrice">${state.selectedSize.price.toLocaleString('en-ZA')}</span> →`;
    return;
  }

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: {
            'Artwork Style':  state.selectedStyle.name,
            'Canvas Size':    state.selectedSize.id,
            'Dimensions':     state.selectedSize.dimensions,
            'Artwork File':   state.selectedResult,
            'Original Photo': state.uploadedPhotoUrl,
            'Order Note':     'CUSTOM ART ORDER — Review artwork file before printing'
          }
        }]
      })
    });
    if (!res.ok) throw new Error('Cart add failed');
    window.location.href = '/cart';
  } catch (_) {
    showCartError('Could not add to cart. Please refresh and try again.');
    btn.disabled = false;
    btn.innerHTML = `Order My Artwork — R<span id="orderPrice">${state.selectedSize.price.toLocaleString('en-ZA')}</span> →`;
  }
}

function showCartError(msg) {
  const existing = document.getElementById('cartError');
  if (existing) existing.remove();
  const el = document.createElement('p');
  el.id = 'cartError';
  el.style.cssText = 'color:#e06060;text-align:center;font-size:14px;margin-top:12px;';
  el.textContent = msg;
  document.getElementById('orderBtn').insertAdjacentElement('afterend', el);
}

/* ─── INIT ──────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  const app = document.getElementById('artworld-studio-app');
  if (!app) return;

  app.innerHTML = buildHTML();
  initUpload();
  initNavigation();
  initStyleSelection();
  initSizeSelection();

  document.getElementById('orderBtn').addEventListener('click', addToCart);

  // Attempt to pre-load Dzine style codes (non-blocking)
  fetchDzineStyles();
});
